import React, { useMemo } from "react";
import "~/assets/styles/old-tailwind.css";
import {
  TrackingProvider,
  initializeAnalytics,
} from "@local-logic/core/tracking";
import ResizeWrapper from "~/components/ResizeWrapper";
import { LocalLogicProvider } from "~/context/clientProvider";
import { useTranslation } from "react-i18next";
import { useStateManagerContext } from "~/context/stateManager";
import useIsMobile from "~/hooks/useIsMobile";
import { ProductNames } from "~/types";
import { ANALYTICS_SETTINGS } from "~/constants/analytics";
import { TitleHeader } from "~/components/TitleHeader";
import * as DemographicsTabs from "./components/Content/DemographicsTabs";
import { Content } from "./components/Content";
import { Footer } from "./components/Footer";
import { LocalDemographicsProvider } from "./context";
import * as styles from "./styles";
import { LocalDemographicsProps } from "./types";

initializeAnalytics(ANALYTICS_SETTINGS.localDemographics.key);

const LocalDemographics = () => {
  const { t } = useTranslation("localDemographics");
  const { sdkState } = useStateManagerContext<LocalDemographicsProps>();
  const isMobile = useIsMobile();

  const clientId = useMemo(() => {
    const clientKey = sdkState.apiKey?.split(".") ?? "";

    return clientKey[0].slice(3);
  }, [sdkState.apiKey]);

  return (
    <ResizeWrapper>
      <LocalLogicProvider
        environment={sdkState.globalOptions?.environment?.api}
        apiKey={sdkState.apiKey}
        appearance={sdkState.globalOptions?.appearance}
        locale={sdkState.globalOptions?.locale}
        product={ProductNames.LOCAL_DEMOGRAPHICS}
      >
        <TrackingProvider
          trackingPipelineSettings={{
            apiKey: sdkState.apiKey || "",
          }}
          analyticsSettings={ANALYTICS_SETTINGS.localDemographics}
          meta={{
            clientId,
            productName: ProductNames.LOCAL_DEMOGRAPHICS,
            productVersion: FULL_APP_VERSION,
          }}
          attributes={{
            location: {
              lat: sdkState.options?.lat,
              lng: sdkState.options?.lng,
            },
            host_url: document.referrer,
            locale: sdkState.globalOptions?.locale,
            isMobile,
          }}
          flags={{
            isDebugMode:
              import.meta.env.VITE_TRACKING_PIPELINE_DEBUG === "true",
          }}
        >
          <LocalDemographicsProvider>
            <div className={styles.container}>
              <div className={styles.tabsRoot}>
                <TitleHeader
                  title={
                    typeof sdkState.options?.title !== "undefined"
                      ? sdkState.options.title
                      : t("title")
                  }
                />
                {!isMobile ? (
                  <DemographicsTabs.Tabs />
                ) : (
                  <DemographicsTabs.Select />
                )}
                <Content />
                <Footer />
              </div>
            </div>
          </LocalDemographicsProvider>
        </TrackingProvider>
      </LocalLogicProvider>
    </ResizeWrapper>
  );
};

export default LocalDemographics;
